import { z } from "zod";
import { KYC_CAPACITIES, KYC_DOCUMENT_KINDS } from "./kyc";

/**
 * KYC compliance shared contract (docs/specs/kyc-aml-profile.md, "Compliance
 * API", PR 6a/6b): the officer's `/compliance/kyc/*` wire shapes. Internal
 * statuses and risk appear here — never in the customer's `/kyc/me`. apps/api
 * mirrors these in `apps/api/src/kyc/dto/kyc-compliance.dto.ts`;
 * `kyc-contract.spec.ts` parses the service's output with them.
 */

export const KYC_INTERNAL_STATUSES = [
  "in_progress",
  "screening",
  "in_review",
  "action_needed",
  "verified",
  "update_due",
  "restricted",
  "declined",
] as const;
export const KYC_RISK_LEVELS = ["low", "medium", "high"] as const;
export const KYC_PARTY_ROLES = ["subject", "principal", "entity", "beneficial_owner"] as const;
export const KYC_SCREENING_RESULTS = ["clear", "possible", "confirmed"] as const;
export const KYC_DECISION_ACTIONS = ["approve", "request_info", "escalate", "decline", "clear_match", "confirm_match"] as const;
/** The lists a manual screening checks, ticked on the checklist. */
export const KYC_SCREENING_LISTS = ["un", "saudi_local", "ofac", "eu", "uk", "pep", "adverse_media"] as const;
export const KYC_RISK_FACTOR_KEYS = [
  "pep",
  "high_risk_country",
  "passport_route",
  "layered_or_foreign_ownership",
  "value_over_5m",
  "value_2m_5m",
  "power_of_attorney",
  "buy_or_sell",
  "edited_ownership_source",
] as const;

export type KycInternalStatus = (typeof KYC_INTERNAL_STATUSES)[number];
export type KycRiskLevel = (typeof KYC_RISK_LEVELS)[number];
export type KycScreeningResult = (typeof KYC_SCREENING_RESULTS)[number];
export type KycDecisionAction = (typeof KYC_DECISION_ACTIONS)[number];
export type KycScreeningList = (typeof KYC_SCREENING_LISTS)[number];
export type KycRiskFactorKey = (typeof KYC_RISK_FACTOR_KEYS)[number];

/** GET /compliance/kyc/access */
export const kycComplianceAccessSchema = z.object({ review: z.boolean(), approveHigh: z.boolean() });
export type KycComplianceAccess = z.infer<typeof kycComplianceAccessSchema>;

/** GET /compliance/kyc/queue — one row per profile waiting for screening or a decision. */
export const kycQueueItemSchema = z.object({
  profileId: z.string().uuid(),
  status: z.enum(KYC_INTERNAL_STATUSES),
  risk: z.enum(KYC_RISK_LEVELS).nullable(),
  capacity: z.enum(KYC_CAPACITIES).nullable(),
  nameAr: z.string().nullable(),
  nameEn: z.string().nullable(),
  submittedAt: z.string().nullable(),
  /** Review deadline (YYYY-MM-DD) in Saudi working days. */
  dueBy: z.string().nullable(),
});
export type KycQueueItem = z.infer<typeof kycQueueItemSchema>;
export const kycQueueSchema = z.array(kycQueueItemSchema);

const kycScreeningSchema = z.object({
  result: z.enum(KYC_SCREENING_RESULTS),
  lists: z.array(z.string()),
  note: z.string().nullable(),
  checkedBy: z.string().nullable(),
  checkedAt: z.string(),
});

/** GET /compliance/kyc/cases/:profileId (and every write's response). */
export const kycCaseResponseSchema = z.object({
  profileId: z.string().uuid(),
  status: z.enum(KYC_INTERNAL_STATUSES),
  risk: z.enum(KYC_RISK_LEVELS).nullable(),
  riskPoints: z.number().int().nullable(),
  capacity: z.enum(KYC_CAPACITIES).nullable(),
  version: z.number().int(),
  submittedAt: z.string().nullable(),
  dueBy: z.string().nullable(),
  riskFactors: z.array(z.object({ key: z.enum(KYC_RISK_FACTOR_KEYS), points: z.number().int().nullable() })),
  /** The version's answers, ID numbers reduced to their last 4. */
  answers: z.record(z.unknown()),
  fieldSources: z.record(z.unknown()),
  parties: z.array(
    z.object({
      id: z.string().uuid(),
      role: z.enum(KYC_PARTY_ROLES),
      nameAr: z.string().nullable(),
      nameEn: z.string().nullable(),
      nationality: z.string().nullable(),
      dob: z.string().nullable(),
      idLast4: z.string().nullable(),
      ownershipPct: z.number().nullable(),
      control: z.string().nullable(),
      isPep: z.boolean(),
      /** Newest first; the first is the party's current result. */
      screenings: z.array(kycScreeningSchema),
    }),
  ),
  decisions: z.array(
    z.object({
      action: z.enum(KYC_DECISION_ACTIONS),
      reviewerId: z.string(),
      fromRisk: z.enum(KYC_RISK_LEVELS).nullable(),
      toRisk: z.enum(KYC_RISK_LEVELS).nullable(),
      note: z.string(),
      decidedAt: z.string(),
      version: z.number().int(),
    }),
  ),
  documents: z.array(z.object({ id: z.string().uuid(), kind: z.enum(KYC_DOCUMENT_KINDS), uploadedAt: z.string() })),
});
export type KycCaseResponse = z.infer<typeof kycCaseResponseSchema>;
export type KycCaseParty = KycCaseResponse["parties"][number];

/** POST /compliance/kyc/cases/:profileId/screenings — one entry per party of the current version. */
export const recordKycScreeningsSchema = z.object({
  results: z
    .array(
      z
        .object({
          partyId: z.string().uuid(),
          result: z.enum(KYC_SCREENING_RESULTS),
          lists: z.array(z.enum(KYC_SCREENING_LISTS)).min(1),
          note: z.string().trim().max(1000).optional(),
        })
        .refine((e) => e.result === "clear" || !!e.note, { message: "describe the match", path: ["note"] }),
    )
    .min(1)
    .max(30),
});
export type RecordKycScreenings = z.infer<typeof recordKycScreeningsSchema>;

/** POST /compliance/kyc/cases/:profileId/decisions — every decision needs a note. */
export const kycDecisionSchema = z.object({
  action: z.enum(KYC_DECISION_ACTIONS),
  note: z.string().trim().min(10).max(2000),
  /** The party whose possible match `clear_match` / `confirm_match` resolves. */
  partyId: z.string().uuid().optional(),
});
export type KycDecisionInput = z.infer<typeof kycDecisionSchema>;
